import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FacebookLogo, InstagramLogo, LinkedinLogo, TwitterLogo, YoutubeLogo, EnvelopeSimple, Phone, MapPin } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SOCIAL_ICONS = {
  facebook: FacebookLogo,
  linkedin: LinkedinLogo,
  twitter:  TwitterLogo,
  instagram: InstagramLogo,
  youtube:  YoutubeLogo,
};

export default function Footer() {
  const [s, setS] = useState({});
  const [offices, setOffices] = useState([]);

  useEffect(() => {
    axios.get(`${API}/public/settings`).then((r) => setS(r.data || {})).catch(() => {});
    axios.get(`${API}/public/pages/home.offices`).then((r) => {
      const list = r.data?.value?.items || r.data?.value?.offices;
      if (Array.isArray(list)) setOffices(list);
    }).catch(() => {});
  }, []);

  // Head office takes precedence; fall back to Settings contact fields
  const head = offices.find((o) => o.is_head_office) || offices[0] || {};
  const contactAddress = head.address || s.contact_address;
  const contactPhone   = head.phone   || s.contact_phone;
  const contactEmail   = head.email   || s.contact_email;

  const social = s.social_links || {};
  const year = new Date().getFullYear();
  const title = s.site_title || "Bonheur Pharma";
  const logoUrl = s.logo_url ? (s.logo_url.startsWith("http") ? s.logo_url : `${BACKEND}${s.logo_url}`) : null;

  return (
    <footer className="bg-[#0F172A] text-white" data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand column */}
          <div>
            <Link to="/" className="flex items-center gap-2">
              {logoUrl ? (
                <img src={logoUrl} alt={title} className="h-16 w-auto object-contain" data-testid="footer-logo" />
              ) : (
                <>
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#C2410C] to-[#0EA5E9] flex items-center justify-center text-white font-bold text-lg" style={{ fontFamily: "'Manrope', sans-serif" }}>B</div>
                  <div className="leading-tight">
                    <div className="text-[9px] tracking-[0.3em] uppercase text-white/60">Pharmaceuticals</div>
                    <div className="font-bold text-base" style={{ fontFamily: "'Manrope', sans-serif" }}>{title}</div>
                  </div>
                </>
              )}
            </Link>
            {s.site_tagline && <p className="mt-4 text-sm text-white/70 leading-relaxed">{s.site_tagline}</p>}
          </div>

          {/* Explore */}
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 font-semibold">Explore</div>
            <ul className="mt-4 space-y-2 text-sm">
              {[["/about","About us"],["/products","Products"],["/blogs","Insights"],["/careers","Careers"],["/contact","Contact"]].map(([to,l]) => (
                <li key={to}><Link to={to} className="text-white/80 hover:text-[#FB923C] transition-colors duration-200">{l}</Link></li>
              ))}
            </ul>
          </div>

          {/* Contact — head office details */}
          <div data-testid="footer-contact">
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 font-semibold">Contact</div>
            {(head.name || head.title) && <div className="mt-3 text-xs uppercase tracking-[0.15em] text-[#FB923C] font-semibold">{head.is_head_office ? "Head office" : (head.name || head.title)}</div>}
            <ul className="mt-2 space-y-3 text-sm text-white/80">
              {contactAddress && <li className="flex items-start gap-2"><MapPin size={16} className="mt-0.5 text-[#FB923C] flex-shrink-0" /><span className="leading-relaxed whitespace-pre-line">{contactAddress}</span></li>}
              {contactPhone && <li className="flex items-center gap-2"><Phone size={16} className="text-[#FB923C] flex-shrink-0" /><a href={`tel:${String(contactPhone).replace(/[^+\d]/g, "")}`} className="hover:text-white">{contactPhone}</a></li>}
              {contactEmail && <li className="flex items-center gap-2"><EnvelopeSimple size={16} className="text-[#FB923C] flex-shrink-0" /><a href={`mailto:${contactEmail}`} className="hover:text-white">{contactEmail}</a></li>}
              {head.hours && <li className="text-xs text-white/60 pl-6">{head.hours}</li>}
            </ul>
          </div>

          {/* Follow — social links */}
          <div data-testid="footer-social">
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 font-semibold">Follow</div>
            <div className="mt-4 flex flex-wrap gap-2">
              {Object.entries(social).filter(([, v]) => !!v).map(([k, v]) => {
                const Icon = SOCIAL_ICONS[k.toLowerCase()];
                if (!Icon) return null;
                return (
                  <a
                    key={k}
                    href={v}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={k}
                    data-testid={`footer-social-${k}`}
                    className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#C2410C] flex items-center justify-center transition-colors duration-200"
                  >
                    <Icon size={16} />
                  </a>
                );
              })}
            </div>
            {s.newsletter_text && <p className="mt-5 text-xs text-white/60 leading-relaxed">{s.newsletter_text}</p>}
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row gap-2 justify-between text-xs text-white/50">
          <div>© {year} {title}. All rights reserved.</div>
          <div className="flex gap-5">
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
